/**
 * <variant-picker>
 *
 * Generic option-iterator for Shopify products. Renders one row per
 * product.options_with_values[]. First option always interactive;
 * position 2+ gated until prior options resolved.
 *
 * Reads product JSON from a child <script type="application/json" data-product-json>.
 *
 * Dispatched events (on document):
 *   variant:changed  { variantId, available, image, price, compare_at_price, sectionId }
 */

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._product = null;
    this._sectionId = '';
    this._currentVariant = null;
    this._selections = {};  // { 'option1': '300 ml', 'option2': 'Tomato', ... }
  }

  connectedCallback() {
    this._sectionId = this.getAttribute('data-section-id') || '';
    const jsonNode = this.querySelector('script[data-product-json]');
    if (!jsonNode) return;
    try {
      this._product = JSON.parse(jsonNode.textContent);
    } catch (err) {
      console.error('[variant-picker] failed to parse product JSON', err);
      return;
    }

    this._readUrlSelection();
    this._bindRadioEvents();
    this._refresh();
  }

  // Read ?variant=<id> from URL and pre-select if found.
  _readUrlSelection() {
    let urlVariantId = null;
    try {
      urlVariantId = parseInt(new URLSearchParams(window.location.search).get('variant'), 10);
    } catch (_) { /* noop */ }

    let targetVariant = null;
    if (urlVariantId && this._product.variants) {
      targetVariant = this._product.variants.find(v => v.id === urlVariantId) || null;
    }
    if (!targetVariant) {
      const sv = this._product.selected_or_first_available_variant;
      if (sv && sv.id && this._product.variants) {
        targetVariant = this._product.variants.find(v => v.id === sv.id) || null;
      }
    }
    if (!targetVariant && this._product.variants && this._product.variants.length > 0) {
      targetVariant = this._product.variants[0];
    }
    if (!targetVariant) return;

    // Push the option values from this variant into selections.
    const options = this._product.options_with_values || [];
    options.forEach((opt, idx) => {
      const optionKey = `option${opt.position}`;
      const value = targetVariant[optionKey];
      if (value) {
        this._selections[optionKey] = value;
      }
    });

    // Reflect into the DOM checkboxes/radios.
    Object.keys(this._selections).forEach(optionKey => {
      const value = this._selections[optionKey];
      const input = this.querySelector(`input[type="radio"][data-option-key="${optionKey}"][value="${CSS.escape(value)}"]`);
      if (input) input.checked = true;
    });
  }

  _bindRadioEvents() {
    this.addEventListener('change', this._onRadioChange.bind(this));
    /* Variant-image thumbnail row (2026-07-12): buttons alongside the pill/
       swatch radio row. On click, find the matching pill radio and dispatch
       `change` on it so the existing selection pipeline handles the update. */
    this.addEventListener('click', this._onThumbnailClick.bind(this));
  }

  _onThumbnailClick(e) {
    const thumb = e.target.closest('[data-variant-image-target]');
    if (!thumb) return;
    e.preventDefault();
    const optionKey = thumb.dataset.variantImageTarget;
    const value = thumb.dataset.variantImageValue;
    if (!optionKey || !value) return;
    const radio = this.querySelector(
      `input[type="radio"][data-option-key="${optionKey}"][value="${CSS.escape(value)}"]`
    );
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (radio && radio.checked) {
      /* Already selected but keep aria-pressed in sync on re-click. */
      this._syncThumbnails(optionKey, value);
    }
  }

  /* Toggle aria-pressed on the variant-image thumbnails to reflect the
     newly-selected value for its option group. Called from _onRadioChange
     so BOTH pill clicks and thumbnail clicks (which dispatch change on the
     radio) end up updating the thumbnail visual state. */
  _syncThumbnails(optionKey, value) {
    this.querySelectorAll(`[data-variant-image-target="${optionKey}"]`).forEach(btn => {
      btn.setAttribute(
        'aria-pressed',
        btn.dataset.variantImageValue === value ? 'true' : 'false'
      );
    });
  }

  _onRadioChange(e) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;
    const optionKey = target.getAttribute('data-option-key');
    if (!optionKey) return;
    this._selections[optionKey] = target.value;
    this._syncThumbnails(optionKey, target.value);
    this._refresh();
  }

  _allSelected() {
    const options = this._product.options_with_values || [];
    return options.every(opt => !!this._selections[`option${opt.position}`]);
  }

  _resolveVariant() {
    if (!this._allSelected()) return null;
    const variants = this._product.variants || [];
    return variants.find(v => {
      const options = this._product.options_with_values || [];
      return options.every(opt => v[`option${opt.position}`] === this._selections[`option${opt.position}`]);
    }) || null;
  }

  // Re-evaluate gating, available swatches, and the resolved variant.
  _refresh() {
    const options = this._product.options_with_values || [];
    options.forEach((opt, idx) => {
      const fieldset = this.querySelector(`fieldset[data-option-position="${opt.position}"]`);
      if (!fieldset) return;

      // Gating: enable only if all PRIOR options are selected.
      const priorAllSelected = options.slice(0, idx).every(prevOpt =>
        !!this._selections[`option${prevOpt.position}`]
      );
      fieldset.setAttribute('aria-disabled', priorAllSelected ? 'false' : 'true');

      // Help text for gated fieldset.
      const helpText = this.querySelector(`[data-pick-prior-first="option${opt.position}"]`);
      if (helpText) {
        if (priorAllSelected || idx === 0) {
          helpText.setAttribute('hidden', '');
        } else {
          const priorOpt = options[idx - 1];
          helpText.removeAttribute('hidden');
          helpText.textContent = helpText.dataset.template
            ? helpText.dataset.template.replace('__NAME__', priorOpt.name.toLowerCase())
            : `Pick a ${priorOpt.name.toLowerCase()} first.`;
        }
      }
    });

    const variant = this._resolveVariant();
    if (variant) {
      this._currentVariant = variant;
      this._syncUrl(variant.id);
      this._dispatch(variant);
    }
  }

  _syncUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    } catch (_) { /* noop */ }
  }

  _dispatch(variant) {
    const detail = {
      variantId: variant.id,
      available: !!variant.available,
      image: variant.featured_media || variant.featured_image || null,
      price: variant.price,
      price_formatted: variant.price_formatted || null,
      compare_at_price: variant.compare_at_price || null,
      compare_at_price_formatted: variant.compare_at_price_formatted || null,
      option1: variant.option1 || null,
      option2: variant.option2 || null,
      option3: variant.option3 || null,
      sectionId: this._sectionId,
    };
    document.dispatchEvent(new CustomEvent('variant:changed', { detail, bubbles: false }));
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
